import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "../../../../../../index";
import {
  getItps,
  getItpsByProjectAndType,
  createItp,
  updateItp,
  deleteItp,
} from "./api";
import { useParams } from "react-router-dom";
import { usePCS } from "../PCSContext";
import { useMemo } from "react";

export function useGetItps() {
  return useQuery({
    queryKey: ["itps", "all"],
    queryFn: getItps,
    staleTime: 100000,
  });
}

export function useGetItpsByProjectAndType(
  type,
  plant = false,
  projectFilter = [],
) {
  const { id: projectId } = useParams();
  const { itpData } = usePCS();

  const plantData = useMemo(() => {
    if (!plant || !itpData || !itpData[type]) {
      return undefined;
    }

    const typeData = itpData[type] || [];

    if (!Array.isArray(projectFilter) || projectFilter.length === 0) {
      return typeData;
    }

    const normalizedFilters = projectFilter.map((f) => String(f));
    return typeData.filter((item) => {
      const idMatch =
        item?.projectId !== undefined &&
        normalizedFilters.includes(String(item.projectId));
      const nameMatch =
        item?.projectName &&
        normalizedFilters.some(
          (filter) => item.projectName.toLowerCase() === filter.toLowerCase(),
        );
      return idMatch || nameMatch;
    });
  }, [plant, itpData, type, projectFilter]);

  return useQuery({
    queryKey: plant
      ? ["itps", "plant", type, projectFilter]
      : ["itps", "project", projectId, type],
    queryFn: () => {
      if (plant && plantData !== undefined) {
        return Promise.resolve(plantData);
      }
      return getItpsByProjectAndType(projectId, type);
    },
    staleTime: 100000,
    enabled: plant ? plantData !== undefined : !!projectId,
  });
}

export function useCreateItp() {
  const { id: projectId } = useParams();

  return useMutation({
    mutationFn: (data) =>
      createItp({ type: data.type, ...data.values, projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries(["itps"]);
    },
  });
}

export function useUpdateItp() {
  return useMutation({
    mutationFn: ({ values }) => updateItp(values.id, { ...values }),
    onSuccess: () => {
      queryClient.invalidateQueries(["itps"]);
    },
  });
}

export function useDeleteItp() {
  return useMutation({
    mutationFn: (id) => deleteItp(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["itps"]);
    },
  });
}
